import React, { useState } from 'react';
import { QrCode, Copy, Plus, Trash2, Printer, CheckSquare, Square } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAlert } from '../../../../context/AlertContext';

const MasterQRCode = ({ t, tenantData, effectiveTenantId, handleUpdateField, canEdit }) => {
    const { showAlert, showConfirm } = useAlert();
    const [newTableName, setNewTableName] = useState('');
    const [selectedTables, setSelectedTables] = useState([]);
    const tables = tenantData?.tables || [];

    const handleAddTable = () => {
        if (!newTableName.trim()) return;
        const newTable = {
            id: Math.random().toString(36).substr(2, 9),
            name: newTableName.trim()
        };
        const updatedTables = [...tables, newTable];
        handleUpdateField('tables', updatedTables, 'Masa eklendi: ' + newTable.name);
        setNewTableName('');
    };

    const handleDeleteTable = async (id) => {
        if (!await showConfirm(t('common.confirmDelete'), { title: 'Masayı Sil', confirmText: 'Sil', type: 'warning' })) return;
        const updatedTables = tables.filter(table => table.id !== id);
        handleUpdateField('tables', updatedTables, 'Masa silindi');
        setSelectedTables(selectedTables.filter(tid => tid !== id));
    };

    const toggleSelectAll = () => {
        if (selectedTables.length === tables.length) {
            setSelectedTables([]);
        } else {
            setSelectedTables(tables.map(t => t.id));
        }
    };

    const toggleSelectTable = (id) => {
        if (selectedTables.includes(id)) {
            setSelectedTables(selectedTables.filter(tid => tid !== id));
        } else {
            setSelectedTables([...selectedTables, id]);
        }
    };

    const getTableUrl = (tableId) => {
        return `https://app.ugra.io/check-in/${tenantData?.slug || effectiveTenantId}?table=${tableId}`;
    };

    const handlePrintSelected = () => {
        if (selectedTables.length === 0) {
            showAlert("Lütfen yazdırılacak masaları seçin.", "warning");
            return;
        }

        const selectedTablesData = tables.filter(t => selectedTables.includes(t.id));

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showAlert("Lütfen açılır pencerelere (pop-up) izin verin.", "warning");
            return;
        }

        const qrHtml = selectedTablesData.map(table => `
            <div class="card">
                <div class="qr-placeholder" data-url="${getTableUrl(table.id)}"></div>
                <h4>${table.name}</h4>
                <p>${tenantData?.name || ''}</p>
            </div>
        `).join('');

        printWindow.document.write(`
            <html>
            <head>
                <title>QR Kodlar - Yazdır</title>
                <script src="https://cdn.jsdelivr.net/npm/qrcode.react@3.1.0/dist/index.min.js"></script>
                <style>
                    body { font-family: system-ui, -apple-system, sans-serif; text-align: center; color: #333; margin: 0; padding: 20px; }
                    .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 20px; }
                    .card { border: 2px dashed #6f4e37; padding: 25px; border-radius: 16px; display: flex; flex-direction: column; align-items: center; page-break-inside: avoid; background: #fff; }
                    h4 { margin: 15px 0 5px; font-size: 20px; font-weight: 800; color: #6f4e37; }
                    p { margin: 0; font-size: 12px; color: #888; }
                    canvas { max-width: 100%; height: auto; padding: 10px; background: #white; border-radius: 8px; }
                    @media print {
                        .no-print { display: none; }
                        body { padding: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="no-print" style="margin-bottom: 20px; padding: 20px; border-bottom: 1px solid #eee; text-align: center; background: #f9f9f9; border-radius: 12px;">
                    <button onclick="window.print()" style="padding: 12px 32px; font-size: 16px; font-weight: 800; cursor: pointer; background: #6f4e37; color: #fff; border: none; border-radius: 12px; box-shadow: 0 4px 12px rgba(111, 78, 55, 0.3);">
                        Yazdır / PDF
                    </button>
                    <p style="margin-top: 10px; color: #666;">Yazıcı hedefi olarak "PDF olarak kaydet" seçebilirsiniz.</p>
                </div>
                <div class="grid">
                    ${qrHtml}
                </div>
                <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></script>
                <script>
                    document.querySelectorAll('.qr-placeholder').forEach(div => {
                        new QRCode(div, {
                            text: div.getAttribute('data-url'),
                            width: 180,
                            height: 180,
                            colorDark : "#000000",
                            colorLight : "#ffffff",
                            correctLevel : QRCode.CorrectLevel.H
                        });
                    });
                    setTimeout(() => { window.print(); }, 1000);
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <section className="card glass">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2rem' }}>
                <QrCode size={24} style={{ color: 'var(--primary)' }} />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('settings.masterQrCode')}</h2>
            </div>

            <div className="qr-section-container" style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
                <div style={{ flexShrink: 0 }}>
                    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', display: 'inline-block', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid #eee' }}>
                        <QRCodeSVG
                            value={`https://app.ugra.io/check-in/${tenantData?.slug || effectiveTenantId}`}
                            size={160}
                            level="H"
                            includeMargin={true}
                        />
                    </div>
                </div>

                <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>{t('settings.masterQrCode')}</h3>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.9rem' }}>
                        {t('settings.masterQrDesc')}
                    </p>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }} className="qr-buttons-container">
                        <button
                            onClick={() => {
                                const win = window.open('', '_blank');
                                win.document.write(`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;"><img src="data:image/svg+xml;base64,${btoa(new XMLSerializer().serializeToString(document.querySelector('.qr-section-container svg')))}" style="width:80%;max-width:500px;"/></body><script>setTimeout(() => { window.print(); window.close(); }, 500);</script></html>`);
                                win.document.close();
                            }}
                            style={{ flex: 1, minWidth: '120px', fontWeight: 700, borderRadius: '14px', height: '44px', background: '#6f4e37', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <Printer size={18} style={{ marginRight: '8px' }} /> {t('common.print')}
                        </button>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(`https://app.ugra.io/check-in/${tenantData?.slug || effectiveTenantId}`);
                                showAlert(t('common.copy'), 'success', 1500);
                            }}
                            style={{ flex: 1, minWidth: '120px', background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border)', borderRadius: '14px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, height: '44px' }}
                        >
                            <Copy size={16} style={{ marginRight: '8px' }} /> {t('common.copyUrl')}
                        </button>
                    </div>
                </div>
            </div>

            {/* MASALAR BÖLÜMÜ */}
            <hr style={{ margin: '3rem 0', borderColor: 'var(--border)', opacity: 0.5 }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <QrCode size={24} style={{ color: 'var(--primary)' }} />
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Masalar ve QR Kodları</h2>
                </div>
                {tables.length > 0 && (
                    <button
                        onClick={toggleSelectAll}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                        {selectedTables.length === tables.length ? <CheckSquare size={16} /> : <Square size={16} />}
                        Tümünü Seç
                    </button>
                )}
            </div>

            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.9rem' }}>
                Müşterilerinizin masalarında doğrudan sipariş verebilmesi veya puan kazanabilmesi için her masaya özel QR kod oluşturabilirsiniz.
            </p>

            {canEdit && (
                <div style={{ display: 'flex', gap: '12px', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        value={newTableName}
                        onChange={(e) => setNewTableName(e.target.value)}
                        placeholder="Masa Adı (Örn: Masa 1, Bahçe 3...)"
                        style={{ flex: 1, minWidth: '200px', padding: '12px', borderRadius: '14px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)' }}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTable()}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleAddTable} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 24px', borderRadius: '14px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', fontWeight: 600, height: '48px' }}>
                            <Plus size={18} /> Ekle
                        </button>
                        <button
                            onClick={handlePrintSelected}
                            disabled={selectedTables.length === 0}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px', padding: '0 24px', borderRadius: '14px',
                                background: selectedTables.length > 0 ? '#6f4e37' : '#6f4e3766',
                                color: 'white', border: 'none', fontWeight: 700, cursor: selectedTables.length > 0 ? 'pointer' : 'not-allowed',
                                height: '48px',
                                transition: 'all 0.2s',
                                boxShadow: selectedTables.length > 0 ? '0 4px 12px rgba(111, 78, 55, 0.2)' : 'none'
                            }}
                        >
                            <Printer size={18} /> Yazdır ({selectedTables.length})
                        </button>
                    </div>
                </div>
            )}

            {tables.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.5rem' }}>
                    {tables.map(table => {
                        const isSelected = selectedTables.includes(table.id);
                        return (
                            <div
                                key={table.id}
                                onClick={() => toggleSelectTable(table.id)}
                                style={{
                                    background: 'var(--bg-card)',
                                    border: isSelected ? '2px solid var(--primary)' : '1px solid var(--border)',
                                    borderRadius: '16px',
                                    padding: '1.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: isSelected ? '0 10px 20px rgba(var(--primary-rgb), 0.1)' : 'var(--shadow-sm)'
                                }}
                            >
                                <div style={{ position: 'absolute', top: '1rem', left: '1rem' }}>
                                    {isSelected ? <CheckSquare size={20} style={{ color: 'var(--primary)' }} /> : <Square size={20} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />}
                                </div>

                                {canEdit && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTable(table.id);
                                        }}
                                        style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                        title={t('common.delete')}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                )}

                                <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #eee', marginTop: '1rem' }}>
                                    <QRCodeSVG
                                        value={getTableUrl(table.id)}
                                        size={120}
                                        level="H"
                                        includeMargin={true}
                                    />
                                </div>
                                <h4 style={{ fontWeight: 600, fontSize: '1.1rem', margin: '0.5rem 0 0', textAlign: 'center' }}>{table.name}</h4>

                                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(getTableUrl(table.id));
                                            showAlert(t('common.copy'), 'success', 1500);
                                        }}
                                        style={{ flex: 1, padding: '8px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', cursor: 'pointer' }}
                                    >
                                        <Copy size={14} /> URL
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--bg-main)', borderRadius: '12px', border: '1px dashed var(--border)', color: 'var(--text-muted)' }}>
                    Henüz hiç masa eklenmedi. Yeni bir masa ekleyerek spesifik QR kodlar oluşturabilirsiniz.
                </div>
            )}

            <style>{`
                @media (max-width: 600px) {
                    .qr-section-container { flex-direction: column !important; text-align: center !important; gap: 1.5rem !important; }
                    .qr-buttons-container { width: 100% !important; }
                    .qr-buttons-container button { width: 100% !important; flex: none !important; }
                }
            `}</style>
        </section>
    );
};

export default MasterQRCode;